import React, { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import { FaUserShield, FaSearch, FaLayerGroup } from "react-icons/fa";
import { getGroupedPermissions, getRoleMatrix, updateRoleMatrix } from "../services/rbacservice";
import { useAuth } from "../context/authcontext";
import Modal from "../modal/modal";
import "./role_management.css";

export default function RoleMatrixModal({ role, onClose, onSaveSuccess }) {
  const { refreshUser } = useAuth();
  const [groupedPerms, setGroupedPerms] = useState([]);
  const [selectedPermIds, setSelectedPermIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [permSearch, setPermSearch] = useState("");

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [groups, matrix] = await Promise.all([
        getGroupedPermissions(),
        getRoleMatrix(role.id),
      ]);
      setGroupedPerms(groups || []);
      setSelectedPermIds(new Set((matrix || []).map((p) => p.id)));
    } catch (err) {
      toast.error("Failed to load permission matrix");
    } finally {
      setLoading(false);
    }
  }, [role?.id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const togglePermission = (id) => {
    const next = new Set(selectedPermIds);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelectedPermIds(next);
  };

  const toggleModuleAll = (modulePerms) => {
    const allIds = modulePerms.map((p) => p.id);
    const allSelected = allIds.every((id) => selectedPermIds.has(id));
    const next = new Set(selectedPermIds);
    allIds.forEach((id) => allSelected ? next.delete(id) : next.add(id));
    setSelectedPermIds(next);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateRoleMatrix(role.id, Array.from(selectedPermIds));
      if (typeof refreshUser === "function") await refreshUser();
      toast.success(`Permissions updated for role '${role.name}'`);
      onSaveSuccess();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save permissions");
    } finally {
      setSaving(false);
    }
  };

  const filteredGroups = groupedPerms.map((group) => {
    if (!permSearch.trim()) return group;
    const q = permSearch.toLowerCase();
    const matchesGroup = group.module.toLowerCase().includes(q);
    const matchedPerms = group.permissions.filter(
      (p) =>
        p.name.toLowerCase().includes(q) ||
        p.codename.toLowerCase().includes(q) ||
        (p.description && p.description.toLowerCase().includes(q)) ||
        matchesGroup
    );
    return matchedPerms.length ? { ...group, permissions: matchedPerms } : null;
  }).filter(Boolean);

  const formatModuleTitle = (mod) => {
    const map = {
      authentication: "User & Account Management",
      permissions_manager: "Security & RBAC Management",
      employee: "Employee Management",
      department: "Department Management",
      designation: "Designation Management",
      attendance: "Attendance Management",
      leave: "Leave Management",
      regularization: "Attendance Regularizations",
      project: "Project Management",
      task: "Task Management",
      team: "Team Management",
      client: "Client Management",
      work_calendar: "Work Calendar & Shifts",
      dashboard: "Analytics & Dashboards",
    };
    return map[mod] || mod.replace(/_/g, " ");
  };

  return (
    <Modal title={`Permission Matrix — ${role.name}`} onClose={onClose} size="xl">
      <div className="rbac-attendance-form-body">
        <div className="form-section-card">
          <div className="section-title-premium">
            <FaUserShield /> Role Permission Details
          </div>
          <div className="rbac-selected-summary">
            <span className="rbac-selected-count">{selectedPermIds.size}</span>
            <span>permissions selected for <strong>{role.name}</strong></span>
          </div>
        </div>

        <div className="form-section-card">
          <div className="section-title-premium">
            <FaSearch /> Search Permissions
          </div>
          <div className="rbac-field-with-icon">
            
            <input
              type="text"
              placeholder="🔍 Search permission, module, or codename..."
              value={permSearch}
              onChange={(e) => setPermSearch(e.target.value)}
            />
          </div>
        </div>

        <div className="form-section-card">
          <div className="section-title-premium">
            <FaLayerGroup /> Permission Modules
          </div>

          {loading ? (
            <div className="rbac-state-message">Loading permission matrix...</div>
          ) : filteredGroups.length === 0 ? (
            <div className="rbac-state-message">
              No permissions or modules matching &quot;{permSearch}&quot;
            </div>
          ) : (
            <div className="rbac-module-stack">
              {filteredGroups.map((group) => {
                const allSelected = group.permissions.every((p) => selectedPermIds.has(p.id));
                const selectedCount = group.permissions.filter((p) => selectedPermIds.has(p.id)).length;

                return (
                  <div key={group.module} className="module-group">
                    <div className="module-group-header">
                      <div className="rbac-module-heading">
                        <span className="module-title">{formatModuleTitle(group.module)}</span>
                        <span className={`rbac-count-badge ${allSelected ? "complete" : selectedCount > 0 ? "partial" : ""}`}>
                          {selectedCount} / {group.permissions.length} selected
                        </span>
                      </div>
                      <button
                        type="button"
                        className="module-toggle-btn"
                        onClick={() => toggleModuleAll(group.permissions)}
                        disabled={role.name.toLowerCase() === "super admin"}
                      >
                        {allSelected ? "Deselect All" : "Select All"}
                      </button>
                    </div>

                    <div className="permissions-list">
                      {group.permissions.map((perm) => (
                        <label key={perm.id} className="permission-checkbox-item">
                          <input
                            type="checkbox"
                            checked={selectedPermIds.has(perm.id)}
                            onChange={() => togglePermission(perm.id)}
                            disabled={role.name.toLowerCase() === "super admin"}
                          />
                          <div className="permission-info">
                            <span className="permission-name">{perm.name}</span>
                            <span className="permission-desc">{perm.description || perm.codename}</span>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div className="modal-footer rbac-attendance-footer">
        <div className="modal-actions">
          <button
            className="primary"
            onClick={handleSave}
            disabled={saving || loading || role.name.toLowerCase() === "super admin"}
          >
            {saving ? "Saving..." : "Save Permissions"}
          </button>
          <button className="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </button>
        </div>
      </div>
    </Modal>
  );
}
